import { inject } from '@angular/core'
import { Route, Router } from '@angular/router'
import { AuthGuard } from './auth/auth.guard'
import { NoAuthGuard } from './auth/no-auth.guard'
import { paywallReverseGuard } from './auth/paywall.guard'
import { RedirectGuard } from './auth/redirect.guard'
import { storeGuard } from './auth/store.guard'
import { translationResolver } from './core/resolvers/translation.resolver'
import { requirePlanFeature } from './shared/guards/plan-feature.guard'
import { unsavedChangesGuard } from './shared/guards/unsaved-changes.guard'
import { stepperUnsavedGuard } from './shared/guards/stepper-unsaved.guard'

export const appRoutes: Route[] = [
  {
    // Public landing-page CTA. The sandbox has no merchant session or API dependency.
    path: 'demo',
    loadComponent: () => import('./demo/demo.component').then(m => m.DemoComponent),
    title: 'Demo Store - TakeTheme',
  },
  {
    path: '',
    canActivate: [RedirectGuard],
    pathMatch: 'full',
  },
  {
    path: 'login',
    canActivate: [NoAuthGuard],
    loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
    title: 'Login - TakeTheme',
    resolve: { translations: translationResolver },
    // 'settings' carries the profile.* keys used by the 2FA / backup-code dialogs.
    data: { translationFiles: ['settings'] },
  },
  {
    path: 'verify-sso',
    canActivate: [NoAuthGuard],
    loadComponent: () => import('./auth/verify-sso/verify-sso.component').then(m => m.VerifySsoComponent),
    title: 'Verify SSO - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    path: 'complete-sso',
    canActivate: [NoAuthGuard],
    loadComponent: () => import('./auth/complete-sso/complete-sso.component').then(m => m.CompleteSsoComponent),
    title: 'Complete Registration - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    // No guard: the handoff must always process the one-time code and REPLACE
    // any existing session (NoAuthGuard would bounce an already-authed tab to
    // /home before the exchange runs).
    path: 'impersonate',
    loadComponent: () => import('./auth/impersonate/impersonate.component').then(m => m.ImpersonateComponent),
    title: 'Impersonation - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    path: 'register',
    canActivate: [NoAuthGuard],
    loadComponent: () => import('./auth/register/register.component').then(m => m.RegisterComponent),
    title: 'Register - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    path: 'forgot-password',
    canActivate: [NoAuthGuard],
    loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
    title: 'Forgot Password - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    path: 'reset-password',
    canActivate: [NoAuthGuard],
    loadComponent: () => import('./auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
    title: 'Reset Password - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    path: 'verify-email',
    canActivate: [NoAuthGuard],
    loadComponent: () => import('./auth/verify-email/verify-email.component').then(m => m.VerifyEmailComponent),
    title: 'Verify Email - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    // The recipient's side of a staff invitation. Public: the token is the
    // credential, and the common case is somebody with no account at all.
    path: 'invite/:token',
    loadComponent: () => import('./auth/accept-invitation/accept-invitation.component').then(m => m.AcceptInvitationComponent),
    title: 'Join a store - TakeTheme',
    resolve: { translations: translationResolver },
    data: { translationFiles: ['settings'] },
  },
  {
    // OAuth authorization endpoint (consent screen) for MCP clients. Behind
    // AuthGuard so an anonymous visitor logs in and comes back with the
    // client's query string intact; behind storeGuard because a grant is
    // scoped to a store, and there is nothing to consent to without one.
    path: 'oauth/authorize',
    canActivate: [AuthGuard, storeGuard],
    loadComponent: () => import('./auth/oauth-consent/oauth-consent.component').then(m => m.OauthConsentComponent),
    title: 'Authorize App - TakeTheme',
    resolve: { translations: translationResolver },
    data: { translationFiles: ['oauth-consent'] },
  },
  {
    path: 'select-store',
    canActivate: [AuthGuard],
    loadComponent: () => import('./auth/select-store/select-store.component').then(m => m.SelectStoreComponent),
    title: 'Select Store - TakeTheme',
    resolve: { translations: translationResolver },
  },
  {
    // Post-signup welcome wizard (greeting + store-type prep). Standalone, shown
    // once after registration before the dashboard. New stores are on a 3-day
    // trial, so this is the first thing they see — not the paywall.
    path: 'welcome',
    canActivate: [AuthGuard],
    loadComponent: () => import('./pages/onboarding/welcome/welcome.component').then(m => m.WelcomeComponent),
    title: 'Welcome - TakeTheme',
    resolve: { translations: translationResolver },
    data: { translationFiles: ['store-identity'] },
  },
  {
    // Voluntary upgrade / plan-selection screen. No longer a forced gate — a
    // trialing store reaches it from the trial banner or Settings → Billing.
    // Standalone (outside the dashboard layout) so there is no sidebar chrome.
    // paywallReverseGuard bounces already-paid stores back to /home.
    path: 'onboarding/plan',
    canActivate: [AuthGuard, storeGuard, paywallReverseGuard],
    loadComponent: () => import('./pages/onboarding/onboarding-plan.component').then(m => m.OnboardingPlanComponent),
    title: 'Choose your plan - TakeTheme',
    resolve: { translations: translationResolver },
    data: { translationFiles: ['settings'] },
  },
  {
    path: '',
    // Lazy: the shell (header, side menu, command palette, notifications, the
    // library barrel behind them) is only for signed-in routes. Imported
    // statically it sat in the main bundle's import graph, so /login and
    // /register downloaded and compiled all of it before the form could paint.
    loadComponent: () => import('./shared/components/layout/layout.component').then(m => m.LayoutComponent),
    // An account between signup and its first store has no store for the
    // dashboard shell to render — storeGuard sends it to the onboarding wizard.
    canActivate: [AuthGuard, storeGuard],
    // The command palette lives in the layout, so it renders on every
    // authenticated route — its namespaces load once here rather than being
    // repeated on all ~40 children. Merged translations persist, so a child
    // declaring its own files does not displace these.
    resolve: { translations: translationResolver },
    data: { translationFiles: ['palette', 'lists-navigation'] },
    children: [
      {
        path: 'setup-two-fa',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/setup-two-fa/setup-two-fa.component').then(m => m.SetupTwoFaComponent),
        title: 'Two-Factor Authentication - TakeTheme',
        resolve: { translations: translationResolver },
        data: { translationFiles: ['settings'] },
      },
      {
        path: 'store/create',
        loadComponent: () => import('./pages/create-store/create-store.component').then(m => m.CreateStoreComponent),
        title: 'Create Store - TakeTheme',
        resolve: { translations: translationResolver },
        data: { breadcrumb: 'breadcrumb.createStore', translationFiles: ['store-identity'] },
      },
      {
        path: 'stores',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/all-stores/all-stores.component').then(m => m.AllStoresComponent),
        title: 'All Stores - TakeTheme',
        resolve: { translations: translationResolver },
        data: { breadcrumb: 'breadcrumb.allStores' },
      },
      {
        path: 'notifications',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./shared/components/notifications/see-all-notifications/see-all-notifications.component').then(m => m.SeeAllNotificationsComponent),
        title: 'Notifications - TakeTheme',
        resolve: { translations: translationResolver },
        data: { breadcrumb: 'breadcrumb.notifications' },
      },
      {
        path: 'home',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
        title: 'Home - TakeTheme',
        resolve: { translations: translationResolver },
        data: { breadcrumb: 'breadcrumb.home', translationFiles: ['analytics'] },
      },
      {
        path: 'orders',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/orders/orders.component').then(m => m.OrdersComponent),
        title: 'Orders - TakeTheme',
        resolve: { translations: translationResolver },
        // 'export' powers the ExportComponent dialog opened from this page.
        data: { translationFiles: ['orders', 'export', 'products', 'list'], breadcrumb: 'breadcrumb.orders' },
      },
      {
        path: 'orders/order-details/:id',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/orders/order-details/order-details.component').then(m => m.OrderDetailsComponent),
        title: 'View order - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['orders', 'discounts'],
          breadcrumb: 'breadcrumb.orderDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.orders', url: '/orders' }],
        },
      },
      {
        path: 'orders/order-details/:id/fulfillment-orders/:fulfillmentId',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/order-fulfillment/order-fulfillment.component').then(m => m.OrderFulfillmentComponent),
        title: 'Fulfillment order - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.fulfillment',
          parentBreadcrumbs: [
            { label: 'breadcrumb.orders', url: '/orders' },
            { label: 'breadcrumb.orderDetails', url: '../..' },
          ],
        },
      },
      {
        path: 'orders/order-details/:id/restock',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/order-restock/order-restock.component').then(m => m.OrderRestockComponent),
        title: 'Restock order - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.restock',
          parentBreadcrumbs: [
            { label: 'breadcrumb.orders', url: '/orders' },
            { label: 'breadcrumb.orderDetails', url: '..' },
          ],
        },
      },
      {
        path: 'orders/order-details/:id/refund',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/order-refund/order-refund.component').then(m => m.OrderRefundComponent),
        title: 'Refund order - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.refund',
          parentBreadcrumbs: [
            { label: 'breadcrumb.orders', url: '/orders' },
            { label: 'breadcrumb.orderDetails', url: '..' },
          ],
        },
      },

      {
        path: 'draft-orders',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/drafts/drafts.component').then(m => m.DraftsComponent),
        title: 'Draft orders - TakeTheme',
        data: {
          translationFiles: ['orders', 'list'],
          breadcrumb: 'breadcrumb.draftOrders',
          parentBreadcrumbs: [{ label: 'breadcrumb.orders', url: '/orders' }],
        },
      },
      {
        path: 'draft-orders/:id',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/draft-details/draft-details.component').then(m => m.DraftDetailsComponent),
        title: 'Draft order - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.draftDetails',
          parentBreadcrumbs: [
            { label: 'breadcrumb.orders', url: '/orders' },
            { label: 'breadcrumb.draftOrders', url: '/draft-orders' },
          ],
        },
      },
      {
        path: 'orders/add-order',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/create-order/create-order.component').then(m => m.CreateOrderComponent),
        title: 'Create order - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.createOrder',
          parentBreadcrumbs: [{ label: 'breadcrumb.orders', url: '/orders' }],
        },
      },
      {
        path: 'draft-orders/:id/edit',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/create-order/create-order.component').then(m => m.CreateOrderComponent),
        title: 'Edit draft - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.editDraft',
          parentBreadcrumbs: [
            { label: 'breadcrumb.orders', url: '/orders' },
            { label: 'breadcrumb.draftOrders', url: '/draft-orders' },
          ],
        },
      },
      {
        path: 'orders/order-details/:id/edit-order',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/orders/edit-order/edit-order.component').then(m => m.EditOrderComponent),
        title: 'Edit order - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.editOrder',
          parentBreadcrumbs: [
            { label: 'breadcrumb.orders', url: '/orders' },
            { label: 'breadcrumb.orderDetails', url: '..' },
          ],
        },
      },
      {
        path: 'abandoned',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/abandoned/abandoned.component').then(m => m.AbandonedComponent),
        title: 'Abandoned - TakeTheme',
        data: { translationFiles: ['orders', 'products', 'list'], breadcrumb: 'breadcrumb.abandonedCarts' },
      },
      {
        path: 'export-jobs',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/export-jobs/export-jobs.component').then(m => m.ExportJobsComponent),
        title: 'Export jobs - TakeTheme',
        data: { translationFiles: ['export', 'list'], breadcrumb: 'breadcrumb.exportJobs' },
      },
      {
        path: 'export-jobs/details/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/export-jobs/export-job-detail/export-job-detail.component').then(m => m.ExportJobDetailComponent),
        title: 'Export Job Details - TakeTheme',
        data: {
          translationFiles: ['export'],
          breadcrumb: 'breadcrumb.exportJobDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.exportJobs', url: '/export-jobs' }],
        },
      },

      {
        path: 'abandoned/details/:id',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/abandoned/abandoned-details/abandoned-details.component').then(m => m.AbandonedDetailsComponent),
        title: 'Abandoned Details - TakeTheme',
        data: {
          translationFiles: ['orders'],
          breadcrumb: 'breadcrumb.cartDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.abandonedCarts', url: '/abandoned' }],
        },
      },
      {
        path: 'analytics',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/analytics/analytics.component').then(m => m.AnalyticsComponent),
        title: 'Analytics - TakeTheme',
        data: { translationFiles: ['analytics'], breadcrumb: 'breadcrumb.analytics' },
      },
      {
        path: 'customers',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/customers/customers.component').then(m => m.CustomersComponent),
        title: 'Customers - TakeTheme',
        data: { translationFiles: ['customers', 'list'], breadcrumb: 'breadcrumb.customers' },
      },
      {
        path: 'customers/privacy-requests',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/customers/privacy-requests/privacy-requests.component').then(m => m.PrivacyRequestsComponent),
        title: 'Privacy Requests - TakeTheme',
        data: {
          translationFiles: ['customers', 'list'],
          breadcrumb: 'breadcrumb.privacyRequests',
          parentBreadcrumbs: [{ label: 'breadcrumb.customers', url: '/customers' }],
        },
      },
      {
        path: 'form-submissions',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/form-submissions/form-submissions.component').then(m => m.FormSubmissionsComponent),
        title: 'Form Submissions - Take Theme',
        data: {
          translationFiles: ['form-submissions', 'list'],
          breadcrumb: 'breadcrumb.formSubmissions',
          parentBreadcrumbs: [{ label: 'breadcrumb.customers', url: '/customers' }],
        },
      },
      {
        path: 'leads',
        redirectTo: 'form-submissions',
        pathMatch: 'full',
      },
      {
        path: 'customers/add-customer',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/customers/add-customer/add-customer.component').then(m => m.AddCustomerComponent),
        title: 'Add Customer - TakeTheme',
        data: {
          translationFiles: ['customers', 'orders'],
          breadcrumb: 'breadcrumb.addCustomer',
          parentBreadcrumbs: [{ label: 'breadcrumb.customers', url: '/customers' }],
        },
      },
      {
        path: 'customers/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/customers/edit-customer/edit-customer.component').then(m => m.EditCustomerComponent),
        title: 'Edit Customer - TakeTheme',
        data: {
          translationFiles: ['customers', 'orders'],
          breadcrumb: 'breadcrumb.editCustomer',
          parentBreadcrumbs: [{ label: 'breadcrumb.customers', url: '/customers' }],
        },
      },
      {
        path: 'segments',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/segments/segments.component').then(m => m.SegmentsComponent),
        title: 'Segments - TakeTheme',
        data: { translationFiles: ['segments', 'list'], breadcrumb: 'breadcrumb.segments' },
      },
      {
        path: 'segments/create',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/segments/create-segment/create-segment.component').then(m => m.CreateSegmentComponent),
        title: 'Create Segment - TakeTheme',
        data: {
          translationFiles: ['segments'],
          breadcrumb: 'breadcrumb.createSegment',
          parentBreadcrumbs: [{ label: 'breadcrumb.segments', url: '/segments' }],
        },
      },
      {
        path: 'segments/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/segments/create-segment/create-segment.component').then(m => m.CreateSegmentComponent),
        title: 'Edit Segment - TakeTheme',
        data: {
          translationFiles: ['segments'],
          breadcrumb: 'breadcrumb.editSegment',
          parentBreadcrumbs: [{ label: 'breadcrumb.segments', url: '/segments' }],
        },
      },
      {
        path: 'segments/:id',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/segments/segment-detail/segment-detail.component').then(m => m.SegmentDetailComponent),
        title: 'Segment Details - TakeTheme',
        data: {
          translationFiles: ['segments', 'analytics'],
          breadcrumb: 'breadcrumb.segmentDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.segments', url: '/segments' }],
        },
      },
      {
        path: 'automations',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/automations/automations.routes').then(m => m.AUTOMATIONS_ROUTES),
      },
      {
        path: 'app-maker',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/app-maker/app-maker.routes').then(m => m.APP_MAKER_ROUTES),
      },
      {
        path: 'automation-channels',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/automations/channels/channels.component').then(m => m.ChannelsComponent),
        title: 'Channels - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['automations'],
          breadcrumb: 'breadcrumb.channels',
          parentBreadcrumbs: [{ label: 'breadcrumb.automations', url: '/automations' }],
        },
      },
      {
        // WhatsApp has its own page (connection, numbers, order confirmation, templates);
        // every other channel shares the generic detail page below.
        path: 'automation-channels/whatsapp_cloud',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./pages/automations/channels/whatsapp-connector/whatsapp-connector.component').then(m => m.WhatsAppConnectorComponent),
        title: 'WhatsApp - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['automations'],
          breadcrumb: 'breadcrumb.channelDetail',
          parentBreadcrumbs: [
            { label: 'breadcrumb.automations', url: '/automations' },
            { label: 'breadcrumb.channels', url: '/automation-channels' },
          ],
        },
      },
      {
        path: 'automation-channels/:secretType',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/automations/channels/channel-detail/channel-detail.component').then(m => m.ChannelDetailComponent),
        title: 'Channel - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          // `settings` supplies `domains.verified` + `featureAccess.*` for the
          // custom sending-domain card rendered on the TakeTheme Mail channel.
          translationFiles: ['automations', 'email-domain', 'settings'],
          breadcrumb: 'breadcrumb.channelDetail',
          parentBreadcrumbs: [
            { label: 'breadcrumb.automations', url: '/automations' },
            { label: 'breadcrumb.channels', url: '/automation-channels' },
          ],
        },
      },
      {
        path: 'automation-templates',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/automations/templates/templates.component').then(m => m.TemplatesComponent),
        title: 'Templates - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['automations', 'list'],
          breadcrumb: 'breadcrumb.templates',
          parentBreadcrumbs: [{ label: 'breadcrumb.automations', url: '/automations' }],
        },
      },
      {
        path: 'automation-templates/create',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/automations/templates/template-editor/template-editor.component').then(m => m.TemplateEditorComponent),
        title: 'Create Template - TakeTheme',
        data: {
          translationFiles: ['automations'],
          breadcrumb: 'breadcrumb.createTemplate',
          parentBreadcrumbs: [
            { label: 'breadcrumb.automations', url: '/automations' },
            { label: 'breadcrumb.templates', url: '/automation-templates' },
          ],
        },
      },
      {
        path: 'automation-templates/create-whatsapp',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () =>
          import('./pages/automations/templates/whatsapp-template-form/whatsapp-template-form.component').then(m => m.WhatsAppTemplateFormComponent),
        title: 'Create WhatsApp Template - TakeTheme',
        data: {
          translationFiles: ['automations'],
          breadcrumb: 'breadcrumb.createWhatsAppTemplate',
          parentBreadcrumbs: [
            { label: 'breadcrumb.automations', url: '/automations' },
            { label: 'breadcrumb.templates', url: '/automation-templates' },
          ],
        },
      },
      {
        path: 'automation-templates/:templateId',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/automations/templates/template-detail/template-detail.component').then(m => m.TemplateDetailComponent),
        title: 'Template Detail - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['automations'],
          breadcrumb: 'breadcrumb.templateDetail',
          parentBreadcrumbs: [
            { label: 'breadcrumb.automations', url: '/automations' },
            { label: 'breadcrumb.templates', url: '/automation-templates' },
          ],
        },
      },
      {
        path: 'automation-templates/:templateId/edit',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/automations/templates/template-editor/template-editor.component').then(m => m.TemplateEditorComponent),
        title: 'Edit Template - TakeTheme',
        data: {
          translationFiles: ['automations'],
          breadcrumb: 'breadcrumb.editTemplate',
          parentBreadcrumbs: [
            { label: 'breadcrumb.automations', url: '/automations' },
            { label: 'breadcrumb.templates', url: '/automation-templates' },
          ],
        },
      },
      {
        path: 'products',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/products/products.component').then(m => m.ProductsComponent),
        title: 'Products - TakeTheme',
        data: { translationFiles: ['products', 'list'], breadcrumb: 'breadcrumb.products' },
      },
      {
        path: 'products/add-product',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/products/add-edit-product/add-edit-product.component').then(m => m.AddEditProductComponent),
        title: 'Add Product - TakeTheme',
        data: {
          translationFiles: ['products', 'content'],
          breadcrumb: 'breadcrumb.addProduct',
          parentBreadcrumbs: [{ label: 'breadcrumb.products', url: '/products' }],
        },
      },
      {
        path: 'products/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/products/add-edit-product/add-edit-product.component').then(m => m.AddEditProductComponent),
        title: 'Edit Products - TakeTheme',
        data: {
          translationFiles: ['products', 'content'],
          breadcrumb: 'breadcrumb.editProduct',
          parentBreadcrumbs: [{ label: 'breadcrumb.products', url: '/products' }],
        },
      },
      {
        path: 'products/bulk-edit',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./pages/products/bulk-edit-products/bulk-edit-products.component').then(m => m.BulkEditProductsComponent),
        title: 'Bulk Edit Products - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['products'],
          breadcrumb: 'breadcrumb.bulkEditProducts',
          parentBreadcrumbs: [{ label: 'breadcrumb.products', url: '/products' }],
        },
      },
      {
        path: 'product-feed',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/product-feed/product-feed.component').then(m => m.ProductFeedComponent),
        title: 'Product Feed - TakeTheme',
        data: { translationFiles: ['product-feed', 'list'], breadcrumb: 'breadcrumb.productFeed' },
      },
      {
        path: 'product-feed/create',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/product-feed/feed-create/feed-create.component').then(m => m.FeedCreateComponent),
        title: 'Create Feed - TakeTheme',
        data: {
          translationFiles: ['product-feed'],
          breadcrumb: 'breadcrumb.createFeed',
          parentBreadcrumbs: [{ label: 'breadcrumb.productFeed', url: '/product-feed' }],
        },
      },
      {
        path: 'product-feed/:id',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/product-feed/feed-detail/feed-detail.component').then(m => m.FeedDetailComponent),
        title: 'Feed Details - TakeTheme',
        data: {
          translationFiles: ['product-feed'],
          breadcrumb: 'breadcrumb.feedDetail',
          parentBreadcrumbs: [{ label: 'breadcrumb.productFeed', url: '/product-feed' }],
        },
      },
      {
        path: 'product-feed/:id/edit',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/product-feed/feed-create/feed-create.component').then(m => m.FeedCreateComponent),
        title: 'Edit Feed - TakeTheme',
        data: {
          translationFiles: ['product-feed'],
          breadcrumb: 'breadcrumb.editFeed',
          parentBreadcrumbs: [{ label: 'breadcrumb.productFeed', url: '/product-feed' }],
        },
      },
      {
        path: 'promotions',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/promotions/promotions.component').then(m => m.PromotionsComponent),
        title: 'Promotions - TakeTheme',
        data: { translationFiles: ['promotions', 'list'], breadcrumb: 'breadcrumb.promotions' },
      },
      {
        path: 'promotions/add-promotion',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/promotions/add-edit-promotions/add-edit-promotions.component').then(m => m.AddEditPromotionsComponent),
        title: 'Add Promotion - TakeTheme',
        data: {
          translationFiles: ['promotions', 'products'],
          breadcrumb: 'breadcrumb.addPromotion',
          parentBreadcrumbs: [{ label: 'breadcrumb.promotions', url: '/promotions' }],
        },
      },
      {
        path: 'promotions/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/promotions/add-edit-promotions/add-edit-promotions.component').then(m => m.AddEditPromotionsComponent),
        title: 'Edit Promotion - TakeTheme',
        data: {
          translationFiles: ['promotions', 'products'],
          breadcrumb: 'breadcrumb.editPromotion',
          parentBreadcrumbs: [{ label: 'breadcrumb.promotions', url: '/promotions' }],
        },
      },
      {
        path: 'affiliates',
        // No plan guard: the page renders its own upgrade prompt on sub-Scale
        // plans (same convention as segments / abandoned / discounts).
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/affiliates/affiliates.component').then(m => m.AffiliatesComponent),
        title: 'Affiliates - TakeTheme',
        data: { translationFiles: ['affiliates', 'list'], breadcrumb: 'breadcrumb.affiliates' },
      },
      {
        path: 'affiliates/links/add',
        canActivate: [AuthGuard, requirePlanFeature('affiliates', '/affiliates')],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/affiliates/links/add-edit-link/add-edit-link.component').then(m => m.AddEditLinkComponent),
        title: 'Add Affiliate Link - TakeTheme',
        data: {
          translationFiles: ['affiliates'],
          breadcrumb: 'breadcrumb.addAffiliateLink',
          parentBreadcrumbs: [{ label: 'breadcrumb.affiliates', url: '/affiliates?tab=links' }],
        },
      },
      {
        path: 'affiliates/links/edit/:id',
        canActivate: [AuthGuard, requirePlanFeature('affiliates', '/affiliates')],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/affiliates/links/add-edit-link/add-edit-link.component').then(m => m.AddEditLinkComponent),
        title: 'Edit Affiliate Link - TakeTheme',
        data: {
          translationFiles: ['affiliates'],
          breadcrumb: 'breadcrumb.editAffiliateLink',
          parentBreadcrumbs: [{ label: 'breadcrumb.affiliates', url: '/affiliates?tab=links' }],
        },
      },
      // `/upsells` was the section's URL until the Smart Offers rename (2026-08-30);
      // keep bookmarks, order-details links and emails working. Prefix match keeps the
      // trailing segments (bundles/edit/:id …) and the ?tab= query param.
      { path: 'upsells', redirectTo: 'smart-offers' },
      {
        path: 'smart-offers',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/upsells/upsells.component').then(m => m.UpsellsComponent),
        title: 'Smart Offers - TakeTheme',
        data: { translationFiles: ['upsells'], breadcrumb: 'breadcrumb.upsells' },
      },
      {
        path: 'smart-offers/bundles/add-bundle',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/upsells/bundles/add-edit-bundle/add-edit-bundle.component').then(m => m.AddEditBundleComponent),
        title: 'Add Bundle - TakeTheme',
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.addBundle',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=bundles' }],
        },
      },
      {
        path: 'smart-offers/bundles/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/upsells/bundles/add-edit-bundle/add-edit-bundle.component').then(m => m.AddEditBundleComponent),
        title: 'Edit Bundle - TakeTheme',
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.editBundle',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=bundles' }],
        },
      },
      {
        path: 'smart-offers/volume-discounts/add',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        loadComponent: () =>
          import('./pages/upsells/volume-discounts/add-edit-volume-discount/add-edit-volume-discount.component').then(
            m => m.AddEditVolumeDiscountComponent,
          ),
        title: 'Add Volume Discount - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.addVolumeDiscount',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=volume-discounts' }],
        },
      },
      {
        path: 'smart-offers/volume-discounts/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        loadComponent: () =>
          import('./pages/upsells/volume-discounts/add-edit-volume-discount/add-edit-volume-discount.component').then(
            m => m.AddEditVolumeDiscountComponent,
          ),
        title: 'Edit Volume Discount - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.editVolumeDiscount',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=volume-discounts' }],
        },
      },
      {
        path: 'smart-offers/post-purchase/add-offer',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        loadComponent: () =>
          import('./pages/upsells/post-purchase/add-edit-post-purchase/add-edit-post-purchase.component').then(m => m.AddEditPostPurchaseComponent),
        title: 'Add Post-Purchase Offer - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.addPostPurchase',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=post-purchase' }],
        },
      },
      {
        path: 'smart-offers/post-purchase/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        loadComponent: () =>
          import('./pages/upsells/post-purchase/add-edit-post-purchase/add-edit-post-purchase.component').then(m => m.AddEditPostPurchaseComponent),
        title: 'Edit Post-Purchase Offer - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.editPostPurchase',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=post-purchase' }],
        },
      },
      {
        path: 'smart-offers/popup-offers/add',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        loadComponent: () =>
          import('./pages/upsells/popup-offers/add-edit-popup-offer/add-edit-popup-offer.component').then(m => m.AddEditPopupOfferComponent),
        title: 'Add Pop-up Offer - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.addPopupOffer',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=popup-offers' }],
        },
      },
      {
        path: 'smart-offers/popup-offers/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        loadComponent: () =>
          import('./pages/upsells/popup-offers/add-edit-popup-offer/add-edit-popup-offer.component').then(m => m.AddEditPopupOfferComponent),
        title: 'Edit Pop-up Offer - TakeTheme',
        resolve: { translations: translationResolver },
        data: {
          translationFiles: ['upsells'],
          breadcrumb: 'breadcrumb.editPopupOffer',
          parentBreadcrumbs: [{ label: 'breadcrumb.upsells', url: '/smart-offers?tab=popup-offers' }],
        },
      },
      {
        path: 'blogs',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/content/content.component').then(m => m.ContentComponent),
        title: 'Blogs - TakeTheme',
        data: { translationFiles: ['content'], breadcrumb: 'breadcrumb.blogs' },
      },
      {
        path: 'blogs/add-blog',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/content/add-edit-content/add-edit-content.component').then(m => m.AddEditContentComponent),
        title: 'Add Blog - TakeTheme',
        data: {
          translationFiles: ['content'],
          breadcrumb: 'breadcrumb.addBlog',
          parentBreadcrumbs: [{ label: 'breadcrumb.blogs', url: '/blogs' }],
        },
      },
      {
        path: 'blogs/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/content/add-edit-content/add-edit-content.component').then(m => m.AddEditContentComponent),
        title: 'Edit Blog - TakeTheme',
        data: {
          translationFiles: ['content'],
          breadcrumb: 'breadcrumb.editBlog',
          parentBreadcrumbs: [{ label: 'breadcrumb.blogs', url: '/blogs' }],
        },
      },
      {
        path: 'categories',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/categories/categories.component').then(m => m.CategoriesComponent),
        title: 'Categories - TakeTheme',
        data: { translationFiles: ['content', 'list'], breadcrumb: 'breadcrumb.categories' },
      },
      {
        path: 'menus',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/menus/menus.component').then(m => m.MenusComponent),
        title: 'Menus - TakeTheme',
        data: { translationFiles: ['content'], breadcrumb: 'breadcrumb.menus' },
      },
      {
        path: 'menus/new',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/menus/menu-edit/menu-edit.component').then(m => m.MenuEditComponent),
        title: 'Create menu - TakeTheme',
        data: {
          translationFiles: ['content'],
          breadcrumb: 'breadcrumb.newMenu',
          parentBreadcrumbs: [{ label: 'breadcrumb.menus', url: '/menus' }],
        },
      },
      {
        path: 'menus/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/menus/menu-edit/menu-edit.component').then(m => m.MenuEditComponent),
        title: 'Edit menu - TakeTheme',
        data: {
          translationFiles: ['content'],
          breadcrumb: 'breadcrumb.editMenu',
          parentBreadcrumbs: [{ label: 'breadcrumb.menus', url: '/menus' }],
        },
      },
      {
        path: 'metafields',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/metafields/metafields.component').then(m => m.MetafieldsComponent),
        title: 'Metafields - TakeTheme',
        // Lives under `products` translations because the definitions and the
        // product-form inputs share one key namespace.
        data: { translationFiles: ['products'], breadcrumb: 'breadcrumb.metafields' },
      },
      {
        path: 'pages',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/static-pages/static-pages.component').then(m => m.StaticPagesComponent),
        title: 'Pages - TakeTheme',
        data: { translationFiles: ['pages'], breadcrumb: 'breadcrumb.pages' },
      },
      {
        path: 'pages/new',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/static-pages/add-edit-page/add-edit-page.component').then(m => m.AddEditPageComponent),
        title: 'Create page - TakeTheme',
        data: {
          translationFiles: ['pages'],
          breadcrumb: 'breadcrumb.newPage',
          parentBreadcrumbs: [{ label: 'breadcrumb.pages', url: '/pages' }],
        },
      },
      {
        path: 'pages/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/static-pages/add-edit-page/add-edit-page.component').then(m => m.AddEditPageComponent),
        title: 'Edit page - TakeTheme',
        data: {
          translationFiles: ['pages'],
          breadcrumb: 'breadcrumb.editPage',
          parentBreadcrumbs: [{ label: 'breadcrumb.pages', url: '/pages' }],
        },
      },
      {
        path: 'subcategories',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/categories/subcategories-list/subcategories-list.component').then(m => m.SubcategoriesListComponent),
        title: 'Subcategories - TakeTheme',
        data: {
          translationFiles: ['content'],
          breadcrumb: 'breadcrumb.subcategories',
          parentBreadcrumbs: [{ label: 'breadcrumb.categories', url: '/categories' }],
        },
      },
      // {
      //   path: 'subcategories/add-subcategory',
      //   canActivate: [AuthGuard],
      //   loadComponent: () =>
      //     import('./pages/categories/add-edit-subcategory/add-edit-subcategory.component').then(m => m.AddEditSubcategoryComponent),
      //   title: 'Add Subcategories - TakeTheme',
      //   data: {
      //     breadcrumb: 'breadcrumb.addSubcategory',
      //     parentBreadcrumbs: [
      //       { label: 'breadcrumb.categories', url: '/categories' },
      //       { label: 'breadcrumb.subcategories', url: '/subcategories' },
      //     ],
      //   },
      // },
      // {
      //   path: 'subcategories/edit/:id',
      //   canActivate: [AuthGuard],
      //   loadComponent: () =>
      //     import('./pages/categories/add-edit-subcategory/add-edit-subcategory.component').then(m => m.AddEditSubcategoryComponent),
      //   title: 'Edit Subcategories - TakeTheme',
      //   data: {
      //     breadcrumb: 'breadcrumb.editSubcategory',
      //     parentBreadcrumbs: [
      //       { label: 'breadcrumb.categories', url: '/categories' },
      //       { label: 'breadcrumb.subcategories', url: '/subcategories' },
      //     ],
      //   },
      // },

      {
        path: 'categories/add-category',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/categories/add-edit-category/add-edit-category.component').then(m => m.AddEditCategoryComponent),
        title: 'Add Category - TakeTheme',
        data: {
          translationFiles: ['content', 'products'],
          breadcrumb: 'breadcrumb.addCategory',
          parentBreadcrumbs: [{ label: 'breadcrumb.categories', url: '/categories' }],
        },
      },
      {
        path: 'categories/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/categories/add-edit-category/add-edit-category.component').then(m => m.AddEditCategoryComponent),
        title: 'Edit Category - TakeTheme',
        data: {
          translationFiles: ['content', 'products'],
          breadcrumb: 'breadcrumb.editCategory',
          parentBreadcrumbs: [{ label: 'breadcrumb.categories', url: '/categories' }],
        },
      },
      {
        path: 'discounts',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/discounts/discounts.component').then(m => m.DiscountsComponent),
        title: 'Discounts - TakeTheme',
        data: { translationFiles: ['discounts', 'products', 'list'], breadcrumb: 'breadcrumb.discounts' },
      },
      {
        path: 'support',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/support/support.component').then(m => m.SupportComponent),
        title: 'Support - TakeTheme',
        data: { translationFiles: ['support', 'products', 'list'], breadcrumb: 'breadcrumb.support' },
      },
      {
        path: 'support/tickets/create',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/support/add-edit-ticket/add-edit-ticket.component').then(m => m.AddEditTicketComponent),
        title: 'Create Ticket - TakeTheme',
        data: {
          translationFiles: ['support'],
          breadcrumb: 'breadcrumb.createTicket',
          parentBreadcrumbs: [{ label: 'breadcrumb.support', url: '/support' }],
        },
      },
      {
        path: 'support/tickets/:id',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/support/ticket-details/ticket-details.component').then(m => m.TicketDetailsComponent),
        title: 'Ticket Details - TakeTheme',
        data: {
          translationFiles: ['support'],
          breadcrumb: 'breadcrumb.ticketDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.support', url: '/support' }],
        },
      },
      {
        path: 'support/tickets/:id/edit',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/support/add-edit-ticket/add-edit-ticket.component').then(m => m.AddEditTicketComponent),
        title: 'Edit Ticket - TakeTheme',
        data: {
          translationFiles: ['support'],
          breadcrumb: 'breadcrumb.editTicket',
          parentBreadcrumbs: [{ label: 'breadcrumb.support', url: '/support' }],
        },
      },
      {
        // One screen for every discount type. `?type=` picks which; with no
        // type the wizard opens its picker, which is the same thing the list's
        // create button does.
        path: 'discounts/new',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/discounts/wizard/discount-wizard.component').then(m => m.DiscountWizardComponent),
        title: 'Add Discount - TakeTheme',
        data: {
          // 'products' and 'list' carry the keys the shared product/collection
          // pickers render; without them those controls show raw keys.
          translationFiles: ['discounts', 'products', 'list'],
          breadcrumb: 'breadcrumb.addDiscount',
          parentBreadcrumbs: [{ label: 'breadcrumb.discounts', url: '/discounts' }],
        },
      },
      {
        // Viewing a discount. Read-only, with Edit as a deliberate next step —
        // a list row should not drop the reader into a dirty-tracking form.
        path: 'discounts/:id',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/discounts/details/discount-details.component').then(m => m.DiscountDetailsComponent),
        title: 'Discount - TakeTheme',
        data: {
          translationFiles: ['discounts', 'products', 'list'],
          breadcrumb: 'breadcrumb.discountDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.discounts', url: '/discounts' }],
        },
      },
      {
        path: 'discounts/:id/edit',
        canActivate: [AuthGuard],
        canDeactivate: [stepperUnsavedGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/discounts/wizard/discount-wizard.component').then(m => m.DiscountWizardComponent),
        title: 'Edit Discount - TakeTheme',
        data: {
          translationFiles: ['discounts', 'products', 'list'],
          breadcrumb: 'breadcrumb.editDiscount',
          parentBreadcrumbs: [{ label: 'breadcrumb.discounts', url: '/discounts' }],
        },
      },
      // The four types used to be four pages, so their URLs are in bookmarks,
      // emails and the browser history of everyone who has made a discount.
      // They keep working — the slug becomes `?type=`, the id becomes the edit
      // route — rather than 404ing on a redesign.
      {
        path: 'discounts/add-discount/:type',
        redirectTo: ({ params }) => `/discounts/new?type=${params['type']}`,
      },
      {
        path: 'discounts/edit-discount/:type/:id',
        redirectTo: ({ params }) => `/discounts/${params['id']}/edit`,
      },

      {
        path: 'shipping',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/shipping/shipping-companies.component').then(m => m.ShippingCompaniesComponent),
        title: 'Shipping Companies - TakeTheme',
        data: {
          translationFiles: ['shipping'],
          breadcrumb: 'breadcrumb.shippingCompanies',
          breadcrumbUrl: '/settings?tab=shippingCompanies',
        },
      },
      {
        path: 'shipping/:name/:type',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/shipping/components/shipping-list.component').then(m => m.ShippingListComponent),
        title: 'Shipping List - TakeTheme',
        data: {
          translationFiles: ['shipping', 'payment-gateway'],
          breadcrumb: 'breadcrumb.shippingDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.shippingCompanies', url: '/settings?tab=shippingCompanies' }],
        },
      },

      {
        path: 'payment-gateway',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/payment-gateway/payment-gateway.component').then(m => m.PaymentGatewayComponent),
        title: 'Payment Gateway - TakeTheme',
        data: {
          translationFiles: ['payment-gateway'],
          breadcrumb: 'breadcrumb.paymentGateway',
          breadcrumbUrl: '/settings?tab=paymentGateway',
        },
      },
      {
        path: 'payment-gateway/custom/:slug',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/payment-gateway/payment-details/payment-details.component').then(m => m.PaymentDetailsComponent),
        title: 'Custom Payment Details - TakeTheme',
        data: {
          translationFiles: ['payment-gateway', 'shipping'],
          breadcrumb: 'breadcrumb.customPaymentDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.paymentGateway', url: '/settings?tab=paymentGateway' }],
          isCustomPayment: true,
        },
      },
      {
        path: 'payment-gateway/:type',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/payment-gateway/payment-details/payment-details.component').then(m => m.PaymentDetailsComponent),
        title: 'Payment Details - TakeTheme',
        data: {
          translationFiles: ['payment-gateway', 'shipping'],
          breadcrumb: 'breadcrumb.paymentDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.paymentGateway', url: '/settings?tab=paymentGateway' }],
        },
      },
      {
        path: 'reviews',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/reviews/reviews.component').then(m => m.ReviewsComponent),
        title: 'Reviews - TakeTheme',
        data: { translationFiles: ['reviews', 'list'], breadcrumb: 'breadcrumb.reviews' },
      },
      {
        path: 'reviews/add-review',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/reviews/add-review/add-edit-review.component').then(m => m.AddEditReviewComponent),
        title: 'Add Review - TakeTheme',
        data: {
          translationFiles: ['reviews'],
          breadcrumb: 'breadcrumb.addReview',
          parentBreadcrumbs: [{ label: 'breadcrumb.reviews', url: '/reviews' }],
        },
      },
      {
        path: 'reviews/edit/:id',
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/reviews/add-review/add-edit-review.component').then(m => m.AddEditReviewComponent),
        title: 'Edit Review - TakeTheme',
        data: {
          translationFiles: ['reviews'],
          breadcrumb: 'breadcrumb.editReview',
          parentBreadcrumbs: [{ label: 'breadcrumb.reviews', url: '/reviews' }],
        },
      },
      {
        // Inviting and editing both live inside the settings Users tab now —
        // as a swapped-in view and a modal respectively — so the old routes
        // hand their intent over as query params instead of navigating away.
        path: 'users/invite',
        redirectTo: () => inject(Router).createUrlTree(['/settings'], { queryParams: { tab: 'users', view: 'invite' } }),
      },
      {
        path: 'users/add-user',
        redirectTo: () => inject(Router).createUrlTree(['/settings'], { queryParams: { tab: 'users', view: 'invite' } }),
      },
      {
        path: 'users/edit/:id',
        redirectTo: route =>
          inject(Router).createUrlTree(['/settings'], {
            queryParams: { tab: 'users', user: route.params['id'], modal: 'edit' },
          }),
      },
      {
        path: 'themes',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/themes/themes.component').then(m => m.ThemesComponent),
        title: 'Themes - TakeTheme',
        data: { translationFiles: ['themes'], breadcrumb: 'breadcrumb.themes' },
      },
      {
        path: 'builder',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/theme-builder/theme-builder.component').then(m => m.ThemeBuilderComponent),
        title: 'Theme Builder - TakeTheme',
        data: { translationFiles: ['themes'], breadcrumb: 'breadcrumb.themeBuilder' },
      },
      {
        path: 'tracking',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/tracking/tracking.component').then(m => m.TrackingComponent),
        title: 'Tracking - TakeTheme',
        data: { translationFiles: ['marketing', 'tracking', 'products'], breadcrumb: 'breadcrumb.tracking' },
      },
      {
        // Everything on this page belongs to the person signed in, never to the
        // store. It carries its own rail, so the app side menu steps aside the
        // same way it does for settings.
        // No AuthGuard of its own: the layout route above already applies it to
        // every child, and Angular runs ancestor guards first either way.
        path: 'profile',
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/profile/shell/profile-shell.component').then(m => m.ProfileShellComponent),
        title: 'Profile - TakeTheme',
        data: {
          translationFiles: ['settings', 'lists-navigation'],
          breadcrumb: 'breadcrumb.profile',
          hideAppSideMenu: true,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'personal' },
          {
            path: 'personal',
            loadComponent: () => import('./pages/profile/sections/personal/personal-section.component').then(m => m.PersonalSectionComponent),
            data: { section: 'personal' },
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import('./pages/profile/sections/notifications/notifications-section.component').then(m => m.NotificationsSectionComponent),
            data: { section: 'notifications' },
          },
          {
            path: 'security',
            loadComponent: () => import('./pages/profile/sections/security/security-section.component').then(m => m.SecuritySectionComponent),
            data: { section: 'security' },
          },
          {
            path: 'lists',
            loadComponent: () => import('./pages/profile/sections/lists/lists-section.component').then(m => m.ListsSectionComponent),
            data: { section: 'lists' },
          },
        ],
      },
      {
        path: 'settings',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent),
        canDeactivate: [unsavedChangesGuard],
        title: 'Settings - TakeTheme',
        data: {
          translationFiles: ['settings', 'products', 'payment-gateway', 'shipping', 'webhooks'],
          breadcrumb: 'breadcrumb.settings',
          // Settings carries its own tab navigation; the app side menu steps
          // aside so there is only ever one list of destinations on screen.
          hideAppSideMenu: true,
        },
      },
      {
        path: 'billing',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/settings/billing/billing.component').then(m => m.BillingComponent),
        title: 'Billing - TakeTheme',
        data: { translationFiles: ['settings'], breadcrumb: 'breadcrumb.billing' },
      },
      {
        path: 'plans',
        canActivate: [AuthGuard],
        resolve: { translations: translationResolver },
        loadComponent: () => import('./pages/settings/billing/billing.component').then(m => m.BillingComponent),
        title: 'Billing - TakeTheme',
        data: { translationFiles: ['settings'], breadcrumb: 'breadcrumb.billing' },
      },

      // {
      //   path: 'api-key-management',
      //   canActivate: [AuthGuard],
      //   loadComponent: () => import('./pages/api-key-management/api-key-management.component').then(m => m.ApiKeyManagementComponent),
      //   title: 'Api Key Management - TakeTheme',
      // },
      // {
      //   path: 'api-key-management/view/:id',
      //   canActivate: [AuthGuard],
      //   loadComponent: () => import('./pages/api-key-management/components/view-api-key/view-api-key.component').then(m => m.ViewApiKeyComponent),
      //   title: 'View Api Key Management - TakeTheme',
      // },
    ],
  },
  {
    path: '**',
    canActivate: [RedirectGuard],
    loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
    resolve: { translations: translationResolver },
    data: { translationFiles: ['settings'] },
  },
]
